import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { pick } from "../i18n/strings";

export default function Booking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [exp, setExp] = useState(null);
  const [guests, setGuests] = useState(2);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/experiences/${id}`).then((res) => {
      setExp(res.data);
      if (res.data?.sold_out) {
        navigate(`/experiences/${id}`, { replace: true });
      }
    });
  }, [id, navigate]);

  if (!exp) return null;

  const total = exp.price_eur * guests;

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/bookings", {
        experience_id: exp.id,
        date: exp.date,
        guests,
      });
      navigate(`/payment/${data.id}`);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const dateStr = new Date(exp.date).toLocaleDateString(
    lang === "ca" ? "ca-ES" : lang === "en" ? "en-GB" : "es-ES",
    { weekday: "long", day: "2-digit", month: "long", year: "numeric" },
  );

  return (
    <div className="px-6 pt-6 pb-10" data-testid="booking-page">
      <button
        onClick={() => navigate(-1)}
        data-testid="booking-back"
        className="inline-flex items-center gap-2 text-brand-brown/70 text-xs tracking-[0.25em] uppercase font-bold"
      >
        <ArrowLeft size={14} /> {t("book_now")}
      </button>

      <header className="mt-8">
        <p className="text-[10px] tracking-[0.45em] uppercase font-bold text-brand-tan">
          {t("season_label")} I · {t("chapter")} {exp.chapter}
        </p>
        <h1 className="font-serif text-4xl text-brand-brown mt-2 leading-[1.05]">
          {pick(exp.title, lang)}
        </h1>
      </header>

      <div className="mt-9 rounded-2xl overflow-hidden">
        <img
          src={exp.image}
          alt={pick(exp.title, lang)}
          className="w-full aspect-[16/9] object-cover"
        />
      </div>

      <section className="mt-8">
        <p className="text-[10px] tracking-[0.4em] uppercase font-bold text-brand-tan">
          {t("select_date")}
        </p>
        <div className="mt-3 rounded-xl border border-brand-brown/20 bg-white/60 p-4">
          <p className="font-serif text-2xl text-brand-brown capitalize">
            {dateStr}
          </p>
          <p className="text-xs tracking-widest uppercase text-brand-tan mt-1">
            {exp.location}
          </p>
        </div>
      </section>

      <section className="mt-7">
        <p className="text-[10px] tracking-[0.4em] uppercase font-bold text-brand-tan">
          {t("select_guests")}
        </p>
        <div className="mt-3 flex items-center justify-between rounded-xl border border-brand-brown/20 bg-white/60 p-4">
          <button
            type="button"
            onClick={() => setGuests((g) => Math.max(1, g - 1))}
            data-testid="guests-dec"
            className="w-10 h-10 rounded-full border border-brand-tan/40 text-brand-brown flex items-center justify-center active:scale-95"
          >
            <Minus size={16} />
          </button>
          <div className="text-center">
            <p className="font-serif text-3xl text-brand-brown leading-none">
              {guests}
            </p>
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-tan mt-1.5">
              {t("guests")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setGuests((g) => Math.min(exp.capacity, g + 1))}
            data-testid="guests-inc"
            className="w-10 h-10 rounded-full border border-brand-tan/40 text-brand-brown flex items-center justify-center active:scale-95"
          >
            <Plus size={16} />
          </button>
        </div>
      </section>

      <section className="mt-9 border-t border-brand-tan/30 pt-6">
        <p className="text-[10px] tracking-[0.4em] uppercase font-bold text-brand-tan">
          {t("summary")}
        </p>
        <div className="mt-3 flex justify-between text-brand-brown">
          <span>
            €{exp.price_eur} × {guests}
          </span>
          <span className="font-bold">€{total}</span>
        </div>
        <div className="mt-1 flex justify-between font-serif text-3xl text-brand-brown">
          <span>{t("total")}</span>
          <span data-testid="booking-total">€{total}</span>
        </div>
      </section>

      {error && (
        <p className="text-sm text-destructive mt-4" data-testid="booking-error">
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={busy}
        data-testid="booking-submit"
        className="mt-8 w-full py-4 rounded-2xl bg-brand-brown text-brand-cream font-bold tracking-[0.18em] uppercase text-sm transition-transform active:scale-[0.98] disabled:opacity-60"
      >
        {busy ? "…" : t("proceed_to_payment")}
      </button>
    </div>
  );
}
